// react
import React, { useState, useMemo, useCallback } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Button, Typography, Menu, MenuItem, Box, SpeedDial, SpeedDialAction, useMediaQuery, useTheme } from "@mui/material";
import { Add as AddIcon, Description as DescriptionIcon, Image as ImageIcon, Note as NoteIcon, Settings as SettingsIcon } from "@mui/icons-material";

// A ---------------------------------------------------------------------- M

const CertificationMenu = ({ tag, openDocGenDrawer, openImageGenDrawer, openInfoGenDrawer, openConfigurationGenDrawer }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleSpeedDialOpen = () => setSpeedDialOpen(true);
  const handleSpeedDialClose = () => setSpeedDialOpen(false);

  const handleCertify = useCallback(
    (type) => {
      handleClose();
      setSpeedDialOpen(false);

      const actionMap = {
        PDF: openDocGenDrawer,
        Image: openImageGenDrawer,
        Note: openInfoGenDrawer,
        Configuration: openConfigurationGenDrawer,
      };

      actionMap[type]?.();
    },
    [handleClose, openDocGenDrawer, openImageGenDrawer, openInfoGenDrawer, openConfigurationGenDrawer]
  );

  const menuItems = useMemo(() => {
    const items = [
      { label: t("certify_document"), type: "PDF", icon: <DescriptionIcon /> },
      { label: t("certify_image"), type: "Image", icon: <ImageIcon /> },
      { label: t("certify_note"), type: "Note", icon: <NoteIcon /> },
    ];

    if (tag?.targets?.length) {
      items.push({ label: t("certify_configuration"), type: "Configuration", icon: <SettingsIcon /> });
    }

    return items;
  }, [t, tag]);

  const menuPaperStyle = {
    mt: 1,
    py: 1,
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    backdropFilter: "blur(12px)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
    minWidth: anchorEl ? anchorEl.clientWidth : 200,
    overflow: "hidden",
  };

  return (
    <>
      {isMobile ? (
        <>
          {speedDialOpen && (
            <Box
              sx={{
                position: "fixed",
                inset: 0,
                backdropFilter: "blur(3px)",
                backgroundColor: "rgba(0,0,0,0.25)",
                zIndex: (theme) => theme.zIndex.speedDial - 1,
                transition: "all 0.2s ease",
              }}
              onClick={handleSpeedDialClose}
            />
          )}

          <SpeedDial
            ariaLabel="certification-options"
            sx={{
              position: "fixed",
              bottom: 120,
              right: 15,
            }}
            icon={<AddIcon />}
            onOpen={handleSpeedDialOpen}
            onClose={handleSpeedDialClose}
            open={speedDialOpen}
            FabProps={{
              sx: {
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
                boxShadow: "0 6px 16px rgba(0,0,0,0.22)",
              },
            }}
          >
            {menuItems.map((item) => (
              <SpeedDialAction
                key={item.type}
                icon={item.icon}
                tooltipTitle={
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption">{item.label}</Typography>
                  </Box>
                }
                tooltipOpen
                onClick={() => handleCertify(item.type)}
                sx={{ mb: 1 }}
              />
            ))}
          </SpeedDial>
        </>
      ) : (
        <Button
          aria-controls="certification-menu"
          aria-haspopup="true"
          aria-label={t("certification")}
          startIcon={<AddIcon />}
          variant="contained"
          sx={{ borderRadius: 3, px: 2, textTransform: "none" }}
          onClick={handleClick}
        >
          {t("certification")}
        </Button>
      )}

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} PaperProps={{ sx: menuPaperStyle }}>
        {menuItems.map((item) => (
          <MenuItem
            key={item.type}
            onClick={() => handleCertify(item.type)}
            sx={{
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              py: 1.1,
              px: 1.5,
              display: "flex",
              alignItems: "center",
              transition: "all 0.25s ease",
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
              },
            }}
          >
            {item.icon}
            <Typography sx={{ ml: 1.2, fontSize: "0.92rem", fontWeight: 500 }}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CertificationMenu;
